import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  DollarSign, CheckCircle, AlertCircle, Crown, Megaphone,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { GlassCard, API } from './GlassCard';

export default function UpgradeAndAds() {
  const { getAuthHeaders } = useAuth();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [creatingAd, setCreatingAd] = useState(false);
  const [ads, setAds] = useState([]);
  const [selectedAdType, setSelectedAdType] = useState('featured');
  const [adDuration, setAdDuration] = useState(7);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [boostPackages, setBoostPackages] = useState([]);
  const [boostStatus, setBoostStatus] = useState(null);
  const [buyingBoost, setBuyingBoost] = useState('');
  const location = useLocation();

  useEffect(() => {
    fetchVendor();
    fetchAds();
    fetchStripeStatus();
    fetchBoostPackages();
    fetchBoostStatus();
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const plan = params.get('plan');
    const adSession = params.get('ad_session');
    if (sessionId && plan) pollSubscriptionStatus(sessionId);
    if (adSession) activateBoost(adSession);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVendor = async () => {
    try {
      const response = await fetch(`${API}/vendors/me`, { headers: getAuthHeaders() });
      if (response.ok) setVendor(await response.json());
    } catch (error) {
      console.error('Failed to fetch vendor:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStripeStatus = async () => {
    try {
      const response = await fetch(`${API}/stripe/connect/status`, { headers: getAuthHeaders() });
      if (response.ok) setStripeStatus(await response.json());
    } catch {}
  };

  const fetchAds = async () => {
    try {
      const response = await fetch(`${API}/vendors/me/ads`, { headers: getAuthHeaders() });
      if (response.ok) setAds(await response.json());
    } catch {}
  };

  const fetchBoostPackages = async () => {
    try {
      const res = await fetch(`${API}/ads/packages`);
      if (res.ok) setBoostPackages((await res.json()).packages || []);
    } catch {}
  };

  const fetchBoostStatus = async () => {
    try {
      const res = await fetch(`${API}/ads/my-boost`, { headers: getAuthHeaders() });
      if (res.ok) setBoostStatus(await res.json());
    } catch {}
  };

  const buyBoost = async (pkgId) => {
    setBuyingBoost(pkgId);
    try {
      const res = await fetch(`${API}/ads/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ package_id: pkgId, origin_url: window.location.origin }),
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.checkout_url;
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Failed');
      }
    } catch {
      toast.error('Failed to purchase boost');
    } finally {
      setBuyingBoost('');
    }
  };

  const activateBoost = async (sessionId) => {
    try {
      const res = await fetch(`${API}/ads/activate/${sessionId}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'activated') {
          toast.success(`Boost activated! You're promoted for ${data.days} days.`);
          fetchBoostStatus();
        } else if (data.status === 'already_activated') {
          toast.info('Boost already active!');
        } else {
          setTimeout(() => activateBoost(sessionId), 2000);
        }
      }
    } catch {}
  };

  const handleConnectStripe = async () => {
    setConnectingStripe(true);
    try {
      const response = await fetch(`${API}/stripe/connect/create-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to start Stripe setup');
      }
    } catch (error) {
      toast.error('Failed to connect Stripe');
    } finally {
      setConnectingStripe(false);
    }
  };

  const handleUpgrade = async (level) => {
    if (level === 'standard') {
      setUpgrading(true);
      try {
        const response = await fetch(`${API}/vendors/me/upgrade`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ membership_level: level }),
        });
        if (response.ok) {
          toast.success('Downgraded to Standard');
          fetchVendor();
        }
      } catch {
        toast.error('Failed');
      } finally {
        setUpgrading(false);
      }
      return;
    }

    setUpgrading(true);
    try {
      const response = await fetch(`${API}/stripe/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ plan: level, origin_url: window.location.origin }),
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create payment');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUpgrading(false);
    }
  };

  const pollSubscriptionStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) return;
    try {
      const response = await fetch(`${API}/stripe/subscribe/status/${sessionId}`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        if (data.payment_status === 'paid') {
          toast.success(`${data.plan?.charAt(0).toUpperCase() + data.plan?.slice(1)} membership activated!`);
          fetchVendor();
          return;
        }
        setTimeout(() => pollSubscriptionStatus(sessionId, attempts + 1), 2000);
      }
    } catch {}
  };

  const handleCreateAd = async () => {
    setCreatingAd(true);
    try {
      const response = await fetch(`${API}/vendors/me/run-ad`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ ad_type: selectedAdType, duration_days: adDuration }),
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Ad campaign created! Total cost: $${data.total_cost}`);
        fetchAds();
      } else throw new Error('Failed');
    } catch {
      toast.error('Failed to create ad campaign');
    } finally {
      setCreatingAd(false);
    }
  };

  const plans = [
    { level: 'standard', name: 'Standard', price: 0, features: ['Basic listing', 'Message customers', 'Accept bookings'] },
    { level: 'premium', name: 'Premium', price: 10, features: ['Higher search ranking', 'Priority support', 'Featured in recommendations', 'Analytics dashboard'] },
    { level: 'elite', name: 'Elite', price: 25, features: ['Front page placement', 'Ad eligibility', 'Elite badge', 'Dedicated account manager', 'All Premium features'] },
  ];

  const adTypes = [
    { type: 'featured', name: 'Featured Listing', pricePerDay: 5, description: 'Appear at the top of search results' },
    { type: 'spotlight', name: 'Spotlight', pricePerDay: 10, description: 'Featured on category pages with highlight' },
    { type: 'banner', name: 'Homepage Banner', pricePerDay: 25, description: 'Premium banner placement on homepage' },
  ];

  if (loading) {
    return <div className="space-y-4"><div className="h-12 bg-white/5 rounded-2xl animate-pulse"></div></div>;
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white font-syne" data-testid="upgrade-title">Upgrade & Run Ads</h1>
        <p className="text-sm text-[#9CA3AF] mt-1">Boost your visibility and unlock premium features</p>
      </div>

      {/* Stripe Connect Setup */}
      <GlassCard className="p-6 border-[#8B5CF6]/20" data-testid="stripe-connect-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#635BFF] to-[#00D4FF] flex items-center justify-center">
            <DollarSign size={20} className="text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white font-syne">Stripe Payments</h2>
            <p className="text-xs text-[#9CA3AF]">Connect your bank account to receive payments</p>
          </div>
        </div>
        {stripeStatus?.connected ? (
          <div className="flex items-center gap-3 p-4 rounded-xl bg-green-500/10 border border-green-500/20">
            <CheckCircle size={20} className="text-[#86EFAC]" />
            <div>
              <p className="text-[#86EFAC] font-bold text-sm">Stripe Connected</p>
              <p className="text-[#9CA3AF] text-xs">Your account is set up to receive payments from clients.</p>
            </div>
          </div>
        ) : stripeStatus?.has_account && !stripeStatus?.connected ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
              <AlertCircle size={20} className="text-[#FCD34D]" />
              <div>
                <p className="text-[#FCD34D] font-bold text-sm">Setup Incomplete</p>
                <p className="text-[#9CA3AF] text-xs">Finish setting up your Stripe account to start receiving payments.</p>
              </div>
            </div>
            <button onClick={handleConnectStripe} disabled={connectingStripe} className="px-5 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-[#635BFF] to-[#00D4FF] shadow-[0_0_20px_rgba(99,91,255,0.3)] disabled:opacity-50 transition-all text-sm" data-testid="finish-stripe-setup">
              {connectingStripe ? 'Loading...' : 'Complete Stripe Setup'}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-[#9CA3AF] text-sm">
              Connect your Stripe account to receive payments directly from clients. You'll be redirected to Stripe to set up your bank account securely.
            </p>
            <button onClick={handleConnectStripe} disabled={connectingStripe} className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-[#635BFF] to-[#00D4FF] shadow-[0_0_20px_rgba(99,91,255,0.3)] disabled:opacity-50 transition-all text-sm" data-testid="connect-stripe-btn">
              <DollarSign size={16} /> {connectingStripe ? 'Setting up...' : 'Connect Stripe Account'}
            </button>
          </div>
        )}
      </GlassCard>

      {/* Current Plan */}
      <GlassCard className="p-6">
        <h2 className="text-lg font-semibold text-white font-syne mb-3">Current Plan</h2>
        <span className={`inline-flex px-4 py-2 rounded-full font-bold text-sm ${
          vendor?.membership_level === 'elite' ? 'bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white' :
          vendor?.membership_level === 'premium' ? 'bg-[#8B5CF6]/20 text-[#A78BFA] border border-[#8B5CF6]/30' :
          'bg-white/10 text-[#9CA3AF]'
        }`}>
          {vendor?.membership_level?.charAt(0).toUpperCase() + vendor?.membership_level?.slice(1) || 'Standard'}
        </span>
      </GlassCard>

      {/* Upgrade Plans */}
      <GlassCard className="p-6">
        <h2 className="text-lg font-semibold text-white font-syne mb-4">
          <Crown size={18} className="inline mr-2 text-[#A78BFA]" />
          Upgrade Membership
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map((plan) => (
            <div key={plan.level} className={`p-6 rounded-2xl border transition-all duration-300 ${
              vendor?.membership_level === plan.level
                ? 'border-[#8B5CF6] bg-[#8B5CF6]/10 shadow-[0_0_20px_rgba(139,92,246,0.2)]'
                : 'border-white/10 hover:border-white/20 bg-white/5'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-white font-syne">{plan.name}</h3>
                <span className="text-[#A78BFA] font-semibold">{plan.price === 0 ? 'Free' : `$${plan.price}/mo`}</span>
              </div>
              <ul className="space-y-2 mb-5">
                {plan.features.map((feature, i) => (
                  <li key={i} className="text-[#9CA3AF] text-sm flex items-center gap-2">
                    <CheckCircle size={14} className="text-[#A78BFA] flex-shrink-0" /> {feature}
                  </li>
                ))}
              </ul>
              {vendor?.membership_level === plan.level ? (
                <button disabled className="w-full py-2.5 rounded-xl text-sm font-medium text-[#9CA3AF] bg-white/5 cursor-default">Current Plan</button>
              ) : (
                <button onClick={() => handleUpgrade(plan.level)} disabled={upgrading} className="w-full py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_20px_rgba(139,92,246,0.3)] disabled:opacity-50 transition-all">
                  {upgrading ? 'Processing...' : plan.price === 0 ? 'Downgrade' : `Pay $${plan.price}/mo via Stripe`}
                </button>
              )}
            </div>
          ))}
        </div>
      </GlassCard>

      {/* Run Ads */}
      <GlassCard className="p-6">
        <h2 className="text-lg font-semibold text-white font-syne mb-2">
          <Megaphone size={18} className="inline mr-2 text-[#A78BFA]" />
          Run Ads
        </h2>
        <p className="text-[#9CA3AF] text-sm mb-6">Boost your profile visibility by running targeted ad campaigns.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {adTypes.map((ad) => (
            <div key={ad.type} onClick={() => setSelectedAdType(ad.type)} className={`p-4 rounded-xl border cursor-pointer transition-all duration-200 ${
              selectedAdType === ad.type
                ? 'border-[#8B5CF6] bg-[#8B5CF6]/10 shadow-[0_0_15px_rgba(139,92,246,0.2)]'
                : 'border-white/10 hover:border-white/20 bg-white/5'
            }`}>
              <h4 className="font-bold text-white text-sm mb-1">{ad.name}</h4>
              <p className="text-[#9CA3AF] text-xs mb-2">{ad.description}</p>
              <span className="text-[#A78BFA] font-semibold text-sm">${ad.pricePerDay}/day</span>
            </div>
          ))}
        </div>
        <div className="flex items-end gap-4 mb-6">
          <div>
            <Label className="text-[#9CA3AF] text-xs mb-1 block">Duration (days)</Label>
            <Input type="number" min="1" max="30" value={adDuration} onChange={(e) => setAdDuration(parseInt(e.target.value) || 7)} className="bg-white/5 border-white/10 text-white rounded-xl h-11 w-32" />
          </div>
          <div className="pb-2">
            <p className="text-[#9CA3AF] text-sm">
              Total Cost: <span className="text-[#A78BFA] font-bold">${adTypes.find(a => a.type === selectedAdType)?.pricePerDay * adDuration}</span>
            </p>
          </div>
        </div>
        <button onClick={handleCreateAd} disabled={creatingAd} className="px-6 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_20px_rgba(139,92,246,0.3)] disabled:opacity-50 transition-all text-sm">
          {creatingAd ? 'Creating...' : 'Create Ad Campaign'}
        </button>

        {ads.length > 0 && (
          <div className="mt-8">
            <h3 className="font-semibold text-white mb-4">Your Ad Campaigns</h3>
            <div className="space-y-3">
              {ads.map((ad) => (
                <div key={ad.ad_id} className="p-4 rounded-xl bg-white/5 flex items-center justify-between">
                  <div>
                    <span className="text-white text-sm">{ad.ad_type.charAt(0).toUpperCase() + ad.ad_type.slice(1)}</span>
                    <span className="text-[#9CA3AF] text-xs ml-2">({ad.duration_days} days)</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
                      ad.status === 'active' ? 'bg-green-500/15 text-[#86EFAC] border-green-500/20' :
                      ad.status === 'pending_payment' ? 'bg-yellow-500/15 text-[#FCD34D] border-yellow-500/20' :
                      'bg-white/10 text-[#9CA3AF] border-white/10'
                    }`}>{ad.status.replace('_', ' ')}</span>
                    <span className="text-[#9CA3AF] text-sm">${ad.total_cost}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </GlassCard>

      {/* Promoted Listing / Boost Packages */}
      <GlassCard className="p-6 border-[#F59E0B]/20" data-testid="boost-section">
        <h3 className="text-xl font-bold text-white font-syne mb-2 flex items-center gap-2">
          <Megaphone size={20} className="text-[#F59E0B]" /> Promote Your Listing
        </h3>
        <p className="text-[#9CA3AF] text-sm mb-6">Get more visibility — promoted vendors appear first in search results with a special badge.</p>

        {boostStatus?.promoted && (
          <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-[#F59E0B]/10 to-[#EF4444]/10 border border-[#F59E0B]/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 rounded-full bg-gradient-to-r from-[#F59E0B] to-[#EF4444] text-white text-xs font-bold">{boostStatus.badge}</span>
                <span className="text-white text-sm font-medium">Active</span>
              </div>
              <span className="text-[#9CA3AF] text-xs">Expires: {new Date(boostStatus.expires).toLocaleDateString()}</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {boostPackages.map((pkg) => (
            <div key={pkg.id} className={`rounded-xl border p-5 text-center transition-all ${pkg.id === 'featured_30' ? 'border-[#F59E0B]/30 bg-[#F59E0B]/5' : 'border-white/10 bg-white/5'}`} data-testid={`boost-${pkg.id}`}>
              {pkg.id === 'featured_30' && <span className="text-[10px] text-[#F59E0B] font-bold uppercase tracking-wider">Most Popular</span>}
              <h4 className="text-white font-syne font-bold mt-1">{pkg.name}</h4>
              <p className="text-[#F59E0B] text-3xl font-mono font-bold my-2">${pkg.price}</p>
              <p className="text-[#64748B] text-xs mb-4">{pkg.days} days promoted</p>
              <ul className="text-left text-[#9CA3AF] text-xs space-y-1 mb-4">
                <li className="flex items-center gap-1"><CheckCircle size={10} className="text-green-400" /> Appear first in search</li>
                <li className="flex items-center gap-1"><CheckCircle size={10} className="text-green-400" /> "{pkg.badge}" badge on card</li>
                <li className="flex items-center gap-1"><CheckCircle size={10} className="text-green-400" /> More inquiries & bookings</li>
              </ul>
              <button onClick={() => buyBoost(pkg.id)} disabled={buyingBoost === pkg.id} className="w-full py-2.5 rounded-lg bg-gradient-to-r from-[#F59E0B] to-[#EF4444] text-white text-sm font-bold hover:shadow-[0_0_15px_rgba(245,158,11,0.3)] transition-all disabled:opacity-50" data-testid={`buy-boost-${pkg.id}`}>
                {buyingBoost === pkg.id ? 'Redirecting...' : `Buy ${pkg.name}`}
              </button>
            </div>
          ))}
        </div>
      </GlassCard>
    </div>
  );
}
